import { Icon, Box } from 'vopay';
import { useHistory } from 'react-router-dom';
import ElinxForm from './ElinxForm';
import { ElinxInfoSerialized } from './types';
import usePostSendPaylink from '@/hooks/useApi/usePostSendPaylink';
import usePostEmbedUrl from '@/hooks/useApi/usePostEmbedUrl';
import usePaymentMethodCurrencySupport from '@/hooks/useFields/TransactionCreationFields/usePaymentMethodCurrencySupport';
import useUploadDocument from '@/hooks/useApi/useUploadDocument';
import { useDataFetchIndicator } from '@/hooks';
import { useConfirmDialog } from '@/providers/ConfirmDialog.provider';
import { Title } from 'UI';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/providers/Toast.provider';
import useContact from './useContact';

const ElinxCollectContainer: React.FC = () => {
  const history = useHistory();
  const toast = useToast();
  const [, , , sendElinx] = usePostSendPaylink({});
  const [, , , postEmbedUrl] = usePostEmbedUrl({});
  const { isDomesticCurrency } = usePaymentMethodCurrencySupport('Collect');
  const confirmDialog = useConfirmDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.ECollect' });
  const { t: tElinx } = useTranslation('translations', { keyPrefix: 'Elinx' });
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { uploadDocument } = useUploadDocument();
  const { getContact, saveContactEmail } = useContact();

  const onSubmit = async (values: ElinxInfoSerialized) => {
    try {
      setDataFetchingInfo(true, 'loading some data', 'fullScreen');

      const { InvoiceFile, InvoiceName, AdditionalRecipientEmails, ...rest } = values;

      // earthnode (ID-13659) rejects an IframeKey on a request in the account's other currency — the
      // iframe row's methods are shared by every eLinx made with it, and the currency must narrow
      // them — and builds the hosted page itself instead. The Card Settings' brands still reach it
      // through the request body; the method and account-selection choices do not.
      let iframeKey: string | undefined;

      if (isDomesticCurrency(values.Currency)) {
        const embedUrlResponse = await postEmbedUrl({
          ...rest,
          RedirectURL: '',
          RedirectMethod: 'JavascriptMessage',
          ClientControlled: false,
          Language: 'en',
          ClientAccountID: values?.Contact,
          AcceptedCardBrands: values.AcceptedCardBrands,
          CardTypeValidation: values.CardTypeValidation,
        });

        if (!embedUrlResponse.Success) {
          throw new Error(embedUrlResponse.ErrorMessage);
        }

        iframeKey = embedUrlResponse.IframeKey;
      }

      const emailAddress = values.OverrideEmailAddress || values.NewEmailAddress;
      const receiverEmailAddress = [...new Set([emailAddress, ...AdditionalRecipientEmails].filter(Boolean))].join(',');

      const hasInvoice = InvoiceFile !== '' && InvoiceName !== '';

      const documentID = hasInvoice
        ? (
            await uploadDocument({
              DocumentContent: InvoiceFile || '',
              DocumentName: InvoiceName || '',
              DocumentType: 'invoice',
            })
          ).DocumentID
        : '';

      const elinxResponse = await sendElinx({
        ...rest,
        IframeKey: iframeKey,
        ReceiverEmailAddress: receiverEmailAddress,
        ReceiverName:
          values?.ExistingContactName ||
          (values.ContactType === 'business'
            ? values.BusinessName
            : `${values.FirstName || ''} ${values.LastName || ''}`.trim()) ||
          '',
        Amount: values.Amount ? Number(values.Amount) : undefined,
        PaymentType: 'fund',
        ClientAccountID: values?.Contact,
        ReceiverPhoneNumber: values?.ReceiverPhoneNumber?.replace('+', ''),
        BankingPaymentRail: values.PaymentSelectionMethod?.includes('bank') ? values.BankingPaymentRail : undefined,
        DocumentID: documentID,
      });

      if (!elinxResponse.Success) {
        throw new Error(elinxResponse.ErrorMessage);
      }

      const contactResponse = await getContact({
        ContactID: values.Contact,
      });

      if (contactResponse.EmailAddress === '' && values.OverrideEmailAddress) {
        try {
          await saveContactEmail({
            ContactID: values.Contact,
            EmailAddress: values.OverrideEmailAddress,
          });
        } catch {
          toast.neutral(tElinx('savingEmailError', { defaultValue: 'Failed to save the email address' }));
        }
      }

      const sendNotificationMethod = values?.SendNotification ? values?.NotificationMethod : 'none';
      const isScheduled = !!values?.ScheduleStartDate;

      const queryParams = new URLSearchParams({
        scheduled: isScheduled.toString(),
      });

      history.push(
        `/elinx-history/collect/confirmation/${elinxResponse.ELinxRequestID}/${sendNotificationMethod}/none?${queryParams.toString()}`,
      );
    } catch (err) {
      console.error(err);
      const subTitle = err instanceof Error ? err.message : undefined;

      confirmDialog.error(t('FetchingInfoErrorDialog.title'), {
        subTitle,
        cancelLabel: t('FetchingInfoErrorDialog.cancelLabel'),
      });
    } finally {
      setDataFetchingInfo(false);
    }
  };

  return (
    <Box flx="row" jc="center">
      <Box flx="col" jc="center" maxwidth="1121px" boxWidth="100%" p="md">
        <Box flx="col" gapSize="32px">
          <Title>
            <Box ai="baseline" gapSize="4px">
              <Icon icon="eLinxE" height="18px" width="18px" />
              {t('title')}
            </Box>
          </Title>

          <div>{tElinx('Cards.ECollect.description')}</div>
        </Box>

        <Box base="8" pt="xmd">
          <ElinxForm onSubmit={onSubmit} formType="Collect" />
        </Box>
      </Box>
    </Box>
  );
};

export default ElinxCollectContainer;
